import type { PartnerConfig } from "@expert/app-config/types";
import type { ServiceProvider } from "@expert/service-providers";
import { useExpertWorkspaceWebSocket } from "@expert/websocket";
import { createContext, useEffect, useState } from "react";
import { updateDispatcherPartner } from "@/analytics";
import { env } from "@/config";
import { type CreateSdkParams, createAgentSDK } from "@/utils/createAgentSdk";
import { EvlFailedToConnectError } from "../../errorTypes";
import type { AgentSdk } from "../agentSdk";
import type { AgentSdkBase } from "../agentSdkBase";
import { useStatelessChatCallbacks } from "../chat/chatEventCallbacks";
import { getStatelessCacheActions } from "../chat/getStatelessCacheActions";
import type { OrchestratorAgentSdk } from "../orchestrator/orchestratorAgentSdk";

export const AgentSdkContext = createContext<AgentSdk | undefined>(undefined);

export type AgentSdkProviderCreator = (params: CreateSdkParams) => Promise<AgentSdk>;

export interface AgentSdkLoaderProps {
    employeeNum: string | null;
    userFriendlyName?: string;
    serviceProviders: readonly ServiceProvider[];
    partnerConfig?: PartnerConfig;
    createContainer: (name: string) => Promise<HTMLElement>;
    additionalSdks?: AgentSdkBase[];
    onAuthRedirect?: VoidFunction;
    ignoreEvl?: boolean;
    skip?: boolean;
}

export type AgentSdkFailure = "none" | "general" | "evl-failed";

export interface AgentSdkLoaderResult {
    failureType: AgentSdkFailure;
    errorMessage?: string;
    loadingMessage?: string;
    sdk?: AgentSdk;
}

type AudioProvider = "CCP" | "EVL";

function getAudioProviders(serviceProviders: Iterable<ServiceProvider>, partnerConfig?: PartnerConfig) {
    const result = new Set<AudioProvider>();
    for (const serviceProvider of serviceProviders) {
        if (serviceProvider === "TWILIO") result.add("EVL");
        else if (serviceProvider === "AMAZON_CONNECT") result.add(partnerConfig?.features.medialess ? "CCP" : "EVL");
    }
    return result;
}

export function useAgentSdkLoader({
    employeeNum,
    userFriendlyName,
    serviceProviders,
    partnerConfig,
    createContainer,
    additionalSdks,
    onAuthRedirect,
    ignoreEvl = false,
    skip = false,
}: AgentSdkLoaderProps): AgentSdkLoaderResult {
    const [isReady, setIsReady] = useState(false);
    const [errorInitializingMsg, setErrorInitializingMsg] = useState<string | undefined>(undefined);
    const [agentSdk, setAgentSdk] = useState<AgentSdk | undefined>(undefined);
    const [failureType, setFailureType] = useState<AgentSdkFailure>("none");
    const { dataKeysCache, chatMessagesCache } = getStatelessCacheActions();
    const chatEventCallbacks = useStatelessChatCallbacks();
    const webSocket = useExpertWorkspaceWebSocket();
    useEffect(() => {
        const fetchAgentSdk = async (): Promise<{
            sdk?: OrchestratorAgentSdk;
            errorInitializing?: string;
            failureType: AgentSdkFailure;
        }> => {
            try {
                const sdkRes = await createAgentSDK({
                    createContainer,
                    employeeNum,
                    userFriendlyName,
                    serviceProviders,
                    dataKeysCache,
                    webSocket,
                    chatMessagesCache,
                    chatEventCallbacks,
                    additionalSdks,
                    onAuthRedirect,
                    ignoreEvl,
                });

                return { sdk: sdkRes, failureType: "none" };
            } catch (ex) {
                if (ex instanceof EvlFailedToConnectError) {
                    return { errorInitializing: ex.message, failureType: "evl-failed" };
                }

                return {
                    errorInitializing: ex instanceof Error ? ex.message : JSON.stringify(ex),
                    failureType: "general",
                };
            }
        };

        let ignore = false;
        if (!agentSdk && !skip) {
            setIsReady(false);

            fetchAgentSdk()
                .then((res) => {
                    if (!ignore) {
                        if (res.sdk) {
                            setAgentSdk(res.sdk);
                            setFailureType(res.failureType);
                            setIsReady(true);
                        } else if (res.errorInitializing) {
                            setFailureType(res.failureType);
                            setErrorInitializingMsg(res.errorInitializing);
                        }
                    }
                })
                .catch((ex: unknown) => {
                    if (!ignore) {
                        setErrorInitializingMsg(ex instanceof Error ? ex.message : JSON.stringify(ex));
                    }
                });
        }

        return () => {
            ignore = true;
        };
        // oxlint-disable-next-line exhaustive-deps
    }, [agentSdk, employeeNum, userFriendlyName, serviceProviders, createContainer, skip]);

    useEffect(() => {
        if (!agentSdk || !isReady || !!errorInitializingMsg) {
            return;
        }

        const [partner] = agentSdk.getPartners();
        updateDispatcherPartner(partner);
    }, [agentSdk, isReady, errorInitializingMsg]);

    // NOTE: This error message can be set via an async callback in the constructor. That way
    //       if any errors are thrown during an async initialization, we can catch them and display
    //       the message here
    if (failureType !== "none") {
        return { errorMessage: errorInitializingMsg, failureType };
    }

    if (!agentSdk || !isReady) {
        const message =
            env.channel === "voice"
                ? ` make sure you’re connected to ${[...getAudioProviders(serviceProviders, partnerConfig)].join(" and ")}.`
                : "";
        return { loadingMessage: `Getting things ready...${message}`, failureType: "none" };
    }

    return { sdk: agentSdk, failureType: "none" };
}
